import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_NAME = 'Agrozee Organics';
const SITE_URL = 'https://agrozeeorganics.com';
const DEFAULT_TITLE = 'Agrozee Organics | Millets Now Nutrition & Traceability';
const DEFAULT_DESCRIPTION =
  'Agrozee Organics develops millet-based nutrition programs, school meal tracking, NutriPathshala initiatives, and traceable farm-to-school supply chains through Millets Now.';
const LOGO_URL = `${SITE_URL}/logo.png`;

const routeTitles: Record<string, string> = {
  '/': DEFAULT_TITLE,
  '/about': 'About Agrozee Organics | Millets Now',
  '/contact': 'Contact Agrozee Organics',
  '/programs': 'Programs | Agrozee Organics',
  '/nutripathshala': 'NutriPathshala | Agrozee Organics',
  '/meal-distribution': 'Meal Distribution | Agrozee Organics',
  '/awareness-program': 'Awareness Program | Agrozee Organics',
  '/arogya-club': 'Arogya Club | Agrozee Organics',
  '/shop': 'Shop Nutri Dabba Products | Agrozee Organics',
  '/cart': 'Cart | Agrozee Organics',
  '/login': 'Login | Agrozee Organics',
  '/role-selection': 'Portal Selection | Agrozee Organics',
  '/stats-login': 'Statistics Login | Agrozee Organics',
  '/statistics': 'Impact Statistics | Agrozee Organics',
  '/forgot-password': 'Forgot Password | Agrozee Organics',
  '/reset-password': 'Reset Password | Agrozee Organics',
};

const upsertMeta = (selector: string, createAttributes: Record<string, string>, content: string) => {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement('meta');
    Object.entries(createAttributes).forEach(([key, value]) => element?.setAttribute(key, value));
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

const upsertLink = (rel: string, href: string) => {
  let element = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
};

const upsertJsonLd = (id: string, payload: Record<string, unknown>) => {
  let element = document.head.querySelector<HTMLScriptElement>(`script[data-seo-jsonld="${id}"]`);
  if (!element) {
    element = document.createElement('script');
    element.type = 'application/ld+json';
    element.dataset.seoJsonld = id;
    document.head.appendChild(element);
  }
  element.textContent = JSON.stringify(payload);
};

export const SEO: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const pathname = location.pathname === '/' ? '/' : location.pathname.replace(/\/$/, '');
    const title = routeTitles[pathname] || DEFAULT_TITLE;
    const canonicalUrl = pathname === '/' || pathname === '/landing' ? `${SITE_URL}/` : `${SITE_URL}${pathname}`;

    document.title = title;

    upsertMeta('meta[name="application-name"]', { name: 'application-name' }, SITE_NAME);
    upsertMeta('meta[name="description"]', { name: 'description' }, DEFAULT_DESCRIPTION);
    upsertMeta('meta[name="robots"]', { name: 'robots' }, 'index, follow');
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name' }, SITE_NAME);
    upsertMeta('meta[property="og:title"]', { property: 'og:title' }, SITE_NAME);
    upsertMeta('meta[property="og:type"]', { property: 'og:type' }, 'website');
    upsertMeta('meta[property="og:url"]', { property: 'og:url' }, canonicalUrl);
    upsertMeta('meta[property="og:description"]', { property: 'og:description' }, DEFAULT_DESCRIPTION);
    upsertMeta('meta[property="og:image"]', { property: 'og:image' }, `${SITE_URL}/images/agrozee-logo.png`);
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card' }, 'summary_large_image');
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, SITE_NAME);
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description' }, DEFAULT_DESCRIPTION);
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image' }, `${SITE_URL}/images/agrozee-logo.png`);
    upsertLink('canonical', canonicalUrl);

    upsertJsonLd('website', {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: SITE_NAME,
      url: SITE_URL,
    });
    upsertJsonLd('organization', {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
      logo: LOGO_URL,
    });
  }, [location.pathname]);

  return null;
};

export default SEO;
